// Screen 5 — Calorie choice & result (wizard step 4 of 9)
//
// Behavior:
//  * If calculation_basis === 'manual', the estimator is not shown and
//    the customer must enter a custom calorie target (1000..5000).
//  * Otherwise show a calculated estimate from Mifflin–St Jeor.
//  * Changing the source or target invalidates the generation cache.

import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WizardScreen } from '../../components/planner/WizardScreen';
import { FieldError } from '../../components/planner/FieldError';
import { useDraft, STEP_ROUTES } from '../../lib/plannerDraft';
import { estimateCalories, resolveEffectiveBasis } from '../../lib/plannerCalc';

export default function Step5Calories() {
  const nav = useNavigate();
  const { draft, patch, bump } = useDraft();
  const b = draft.body || {};
  const isManual = b.calculation_basis === 'manual';

  const est = useMemo(() => {
    if (isManual) return null;
    const basis = resolveEffectiveBasis(b.sex, b.calculation_basis);
    if (!basis) return null;
    return estimateCalories({
      basis,
      age: Number(b.age),
      height_cm: Number(b.normalized_height_cm),
      weight_kg: Number(b.normalized_weight_kg),
      activity_level: b.activity_level,
      health_goal: draft.health_goal,
    });
  }, [
    isManual, b.sex, b.calculation_basis, b.age,
    b.normalized_height_cm, b.normalized_weight_kg,
    b.activity_level, draft.health_goal,
  ]);

  // On first visit, initialize calorie_source appropriately.
  useEffect(() => {
    if (isManual && draft.calorie_source !== 'custom') {
      patch({ calorie_source: 'custom' }, { invalidateGeneration: true });
    } else if (!isManual && !draft.calorie_source && est?.calorie_target) {
      patch(
        { calorie_source: 'calculated', calorie_target: est.calorie_target },
        { invalidateGeneration: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManual]);

  const source = draft.calorie_source || (isManual ? 'custom' : 'calculated');
  const custom = source === 'custom' || isManual;

  const [customInput, setCustomInput] = useState(
    draft.calorie_target && custom ? String(draft.calorie_target) : '',
  );
  const [showErr, setShowErr] = useState(false);

  // Effective target used for the Continue check.
  const effectiveTarget = custom
    ? Number(customInput)
    : est?.calorie_target ?? null;

  const valid =
    custom
      ? Number.isFinite(effectiveTarget) && effectiveTarget >= 1000 && effectiveTarget <= 5000
      : Number.isFinite(est?.calorie_target);

  const onContinue = () => {
    if (!valid) { setShowErr(true); return; }
    patch(
      {
        calorie_source: custom ? 'custom' : 'calculated',
        calorie_target: custom ? Math.round(effectiveTarget) : est.calorie_target,
      },
      { recompute: true, invalidateGeneration: true },
    );
    bump(5);
    nav(STEP_ROUTES[5]);
  };

  return (
    <WizardScreen
      step={4}
      eyebrow="Calories"
      title="Your calorie target"
      onContinue={onContinue}
    >
      {!isManual && (
        <div
          className="p-5 md:p-6"
          style={{
            border: '1.5px solid var(--line)',
            borderRadius: 'var(--radius)',
            background: '#fff',
          }}
          data-testid="calories-estimate-card"
        >
          <div className="eyebrow">Estimated for you</div>
          <div className="flex flex-wrap items-baseline gap-3 mt-3">
            <div
              className="font-display"
              style={{ fontSize: '56px', lineHeight: 1 }}
              data-testid="calories-estimate-value"
            >
              {est?.calorie_target?.toLocaleString() ?? '—'}
            </div>
            <div className="eyebrow" style={{ color: 'var(--ink)' }}>cal / day</div>
          </div>
          <div
            className="mt-3 text-sm"
            style={{ color: 'var(--text-dim)' }}
            data-testid="calories-estimate-breakdown"
          >
            BMR {est?.bmr ?? '—'} · TDEE {est?.tdee ?? '—'} · goal adjustment{' '}
            {est?.delta_pct > 0 ? `+${est?.delta_pct}` : est?.delta_pct}%
          </div>
          <p className="mt-3 text-xs" style={{ color: 'var(--text-dim)' }}>
            An estimate from Mifflin–St Jeor. Not medical advice.
          </p>
        </div>
      )}

      {isManual && (
        <div
          className="p-4 mb-2"
          style={{
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning)',
            borderRadius: 'var(--radius)',
            color: 'var(--warning)',
          }}
          data-testid="calories-manual-notice"
        >
          You chose to enter your calorie target manually. Enter a value between
          1,000 and 5,000 to continue.
        </div>
      )}

      {!isManual && (
        <fieldset className="mt-8">
          <legend className="font-display uppercase tracking-wider text-sm mb-2">
            Use this estimate or enter your own?
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup">
            {[
              { id: 'calculated', label: 'Use estimate' },
              { id: 'custom', label: 'Enter my own' },
            ].map((o) => {
              const selected = source === o.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() =>
                    patch({ calorie_source: o.id }, { invalidateGeneration: true })
                  }
                  data-testid={`calorie-source-${o.id}-btn`}
                  className="p-4 text-left transition-colors"
                  style={{
                    background: selected ? 'rgba(239,104,32,0.08)' : '#fff',
                    border: selected ? '2px solid var(--orange)' : '1.5px solid var(--line)',
                    borderRadius: 'var(--radius)',
                  }}
                >
                  <div className="font-display uppercase" style={{ fontSize: '18px' }}>
                    {o.label}
                  </div>
                </button>
              );
            })}
          </div>
        </fieldset>
      )}

      {custom && (
        <label className="block mt-6">
          <span className="font-display uppercase tracking-wider text-sm">
            Daily calories (1,000–5,000)
          </span>
          <input
            type="number"
            inputMode="numeric"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            data-testid="calorie-custom-input"
            className="mt-2 w-full md:w-64"
            style={{
              padding: '12px 14px',
              border: '1.5px solid var(--line)',
              borderRadius: 'var(--radius)',
              background: '#fff',
              fontSize: '16px',
            }}
            aria-invalid={showErr && !valid}
          />
          <FieldError id="calorie-error">
            {showErr && !valid ? 'Please enter a value between 1,000 and 5,000.' : null}
          </FieldError>
        </label>
      )}
    </WizardScreen>
  );
}
